import type { VisualizationSpec } from "vega-embed";
import type { FilterScalarApi, StoryFilterApi, StoryFilterCreateRequestApi } from "./storyFilters.types";
import type { ItemFilterState } from "./storyFilterChips.types";
import type {
  ChartDrillDownRequest,
  ChartDrillHierarchy,
  ChartDrillLevel,
  ChartDrillState,
  CrossFilterCondition,
  CrossFilterTrailStep,
  CrossFilterValue,
} from "./crossFilters.types";
import type { StoryFilterBarAppliedFilter } from "../../components/molecules/StoryFilterBar/StoryFilterBar.types";
import type { ChartPointAction } from "../../components/molecules/ChartPointActionMenu/ChartPointActionMenu.types";

/**
 * Chart chips that stand for a trail step carry this prefix so removing the
 * chip removes the step. Include, Exclude and drill chips share it.
 */
export const CROSS_FILTER_CHIP_ID_PREFIX = "cross:";

/** Values named on a filter chip before the rest collapse into "+N more" (same cut-off as the value picker). */
export const FILTER_CHIP_VISIBLE_VALUES = 2;

/**
 * `West, South +2 more` — names the first `FILTER_CHIP_VISIBLE_VALUES` values
 * and counts the rest, so a many-value filter can't stretch a chip or a crumb.
 */
export const toCollapsedValueList = (valueLabels: string[]): string => {
  const shown = valueLabels.slice(0, FILTER_CHIP_VISIBLE_VALUES).join(", ");
  const hiddenCount = valueLabels.length - FILTER_CHIP_VISIBLE_VALUES;
  return hiddenCount > 0 ? `${shown} +${hiddenCount} more` : shown;
};

export interface FilterChipLabelInput {
  fieldLabel: string;
  /** Exclude / "is not" reads "not in". */
  negated: boolean;
  valueLabels: string[];
}

/**
 * Story filter chip copy, e.g. `Region in West, South +2 more`. `title` always
 * names every value so the tooltip shows what "+N more" hides. With no values
 * picked yet the chip shows just the field, as it did before values existed.
 */
export const toFilterChipLabel = ({
  fieldLabel,
  negated,
  valueLabels,
}: FilterChipLabelInput): { label: string; title: string } => {
  if (valueLabels.length === 0) return { label: fieldLabel, title: fieldLabel };
  const prefix = `${fieldLabel} ${negated ? "not in" : "in"} `;
  return { label: prefix + toCollapsedValueList(valueLabels), title: prefix + valueLabels.join(", ") };
};

/** Top-bar Include/Exclude chips carry this prefix so they never collide with column-id global chips. */
export const CROSS_FILTER_GROUP_CHIP_ID_PREFIX = "crossGroup:";

export interface CrossFilterChipGroup {
  chip: StoryFilterBarAppliedFilter;
  /** Every trail step the chip stands for — removing the chip removes them all. */
  stepIds: string[];
}

/**
 * Include/Exclude steps as story-level top-bar chips: one per field + action,
 * values in click order, so including West then South reads
 * `Region in West, South`. The trail keeps one step per click, so the
 * breadcrumb is unaffected. Drill steps are left out — their chip stays on the
 * drilled chart (`toChartDrillChip`).
 */
export const toCrossFilterChipGroups = (
  steps: CrossFilterTrailStep[],
  fieldLabel: (field: string) => string,
): CrossFilterChipGroup[] => {
  const groups = new Map<
    string,
    { field: string; kind: "include" | "exclude"; valueLabels: string[]; stepIds: string[] }
  >();
  steps.forEach((step) => {
    if (step.kind === "drillDown" || !step.field) return;
    const key = `${step.kind}:${step.field}`;
    const group = groups.get(key) ?? { field: step.field, kind: step.kind, valueLabels: [], stepIds: [] };
    const valueLabel = step.value === null ? "Blank" : step.label;
    // Including the same point twice is two steps but one value on the chip.
    if (!group.valueLabels.includes(valueLabel)) group.valueLabels.push(valueLabel);
    group.stepIds.push(step.id);
    groups.set(key, group);
  });
  return Array.from(groups, ([key, group]) => ({
    chip: {
      id: CROSS_FILTER_GROUP_CHIP_ID_PREFIX + key,
      kind: group.kind,
      ...toFilterChipLabel({
        fieldLabel: fieldLabel(group.field),
        negated: group.kind === "exclude",
        valueLabels: group.valueLabels,
      }),
    },
    stepIds: group.stepIds,
  }));
};

/** The words a draft chip shows for an operator id; any other id reads as itself. */
const DRAFT_OPERATOR_TEXT: Readonly<Record<string, string>> = {
  is: "is",
  in: "is",
  is_not: "is not",
  not_in: "is not",
  eq: "is",
  neq: "is not",
};

/** Operator ids from the filter menu that read "not in" on the chip. */
const NEGATED_FILTER_OPERATOR_IDS = ["is_not"];

/**
 * A top-bar filter as its chip. Values are stored as option ids (`west`), so
 * they are looked up to their display labels (`West`); an id with no matching
 * option, like a column missing from the list, falls back to itself so the
 * chip never renders blank.
 */
export const toGlobalFilterChip = (
  filter: { id: string; columnId: string; operatorId: string | null; values: string[] },
  columns: Array<{ id: string; label: string }>,
  valueOptionsByColumn: Record<string, Array<{ value: string; label: string }>>,
): StoryFilterBarAppliedFilter => {
  const options = valueOptionsByColumn[filter.columnId] ?? [];
  const fieldLabel = columns.find((column) => column.id === filter.columnId)?.label ?? filter.columnId;
  const valueLabels = filter.values.map((value) => options.find((option) => option.value === value)?.label ?? value);
  return {
    id: filter.id,
    ...toFilterChipLabel({
      fieldLabel,
      negated: NEGATED_FILTER_OPERATOR_IDS.includes(filter.operatorId ?? ""),
      valueLabels,
    }),
    conditions: [
      {
        field: fieldLabel,
        operator: DRAFT_OPERATOR_TEXT[filter.operatorId ?? ""] ?? filter.operatorId ?? "is",
        value: valueLabels.length > 0 ? (filter.operatorId === "between" ? valueLabels.join(" and ") : toCollapsedValueList(valueLabels)) : "…",
      },
    ],
  };
};

/** Turns a semantic Include/Exclude condition into one visible trail step. */
export const toCrossFilterTrailStep = (
  condition: CrossFilterCondition,
): CrossFilterTrailStep => ({
  id: condition.id,
  kind: condition.operator === "is" ? "include" : "exclude",
  chartId: condition.chartId,
  field: condition.field,
  operator: condition.operator,
  value: condition.value,
  label: condition.label,
});

/** Keeps the clicked breadcrumb step and removes every later exploration step. */
export const truncateCrossFilterTrail = (
  steps: CrossFilterTrailStep[],
  stepId: string,
): CrossFilterTrailStep[] => {
  const stepIndex = steps.findIndex((step) => step.id === stepId);
  return stepIndex < 0 ? steps : steps.slice(0, stepIndex + 1);
};

/** The level below `field` in whichever hierarchy contains it; null at a leaf or an unknown field. */
export const nextDrillLevel = (
  hierarchies: ChartDrillHierarchy[] | undefined,
  field: string | undefined,
): ChartDrillLevel | null => {
  if (!hierarchies || !field) return null;
  for (const hierarchy of hierarchies) {
    const index = hierarchy.levels.findIndex((level) => level.field === field);
    if (index >= 0) return hierarchy.levels[index + 1] ?? null;
  }
  return null;
};

/** Same channel order and discrete types ChartWidget resolves a clicked point's field with. */
const DIMENSION_CHANNELS = ["x", "y", "color", "detail"];
const DIMENSION_TYPES = ["nominal", "ordinal", "temporal"];

/**
 * The chart's first discrete encoding field, i.e. the field a click on this
 * chart reports. Lets the canvas know whether Drill down can go anywhere
 * before the user clicks.
 */
export const chartDimensionField = (spec: VisualizationSpec | undefined): string | null => {
  const encoding = (spec as { encoding?: Record<string, unknown> } | undefined)?.encoding;
  if (!encoding) return null;
  for (const channel of DIMENSION_CHANNELS) {
    const definition = encoding[channel] as { field?: unknown; type?: unknown } | undefined;
    if (typeof definition?.field === "string" && DIMENSION_TYPES.includes(String(definition.type))) {
      return definition.field;
    }
  }
  return null;
};

/** The server trail row that already holds the same Include/Exclude as `body`, or undefined. */
export const findMatchingTrailFilter = (
  trailFilters: StoryFilterApi[],
  body: StoryFilterCreateRequestApi,
): StoryFilterApi | undefined => {
  if (body.kind !== "cross") return undefined;
  const same = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);
  return trailFilters.find(
    (filter) =>
      filter.kind === "cross" &&
      filter.comparator === body.comparator &&
      same(filter.field, body.field) &&
      same(filter.value, body.value) &&
      // The same value clicked in another chart is another step; the old path names no chart.
      (filter.source_canvas_item_id ?? null) === (body.canvas_item_id ?? null),
  );
};

export interface CrossSelection {
  field: string;
  values: FilterScalarApi[];
}

/**
 * Each chart's own clicked point(s), from the cross rows it is the source of, so it
 * can keep that part lit while the rest dims. Rows on one field merge their values.
 * The point is keyed by the chart's axis column (its data rows' key, e.g. `category`),
 * not the field's display name (`Category`), which matches no row and dims every mark.
 */
export const toCrossSelectionByItemId = (
  trailFilters: StoryFilterApi[],
  stateByItemId: Readonly<Record<string, ItemFilterState>>,
): Record<string, CrossSelection> => {
  const byItem: Record<string, CrossSelection> = {};
  trailFilters.forEach((filter) => {
    const itemId = filter.source_canvas_item_id;
    // Exclude and range steps leave nothing to highlight.
    if (filter.kind !== "cross" || filter.comparator !== "in" || !itemId) return;
    const state = stateByItemId[itemId];
    // Only the chart's own axis is lit; a bucketed axis plots under a renamed key.
    if (!state?.axisColumn || state.axisIsBucketed || state.axisField?.model_field_id !== filter.field.model_field_id) {
      return;
    }
    const values = Array.isArray(filter.value) ? filter.value : [filter.value];
    const current = byItem[itemId];
    if (!current || current.field !== state.axisColumn) {
      byItem[itemId] = { field: state.axisColumn, values: [...values] };
      return;
    }
    values.forEach((value) => {
      if (!current.values.includes(value)) current.values.push(value);
    });
  });
  return byItem;
};
